import styled from '@/styles';
import { ActivationBreakdown } from '@/services/dependabilityV2API';
import checkCircleIcon from '@/assets/icons/heroicons-mini/check-circle.png';

// Figma 40013-12759 + color spec: the activation/new-worker overview card shows
// a wrapping row of onboarding-milestone chips (in place of the reliability
// status badges). Every milestone turns green (#44A735) with a check when
// complete. While incomplete:
//   - onboarding is amber (#ECA300) across border, text and dot;
//   - shifts and I-9 read as "in progress" — amber DOT only, with neutral chrome
//     (grey border, dark text), so only the circle carries color;
//   - the remaining pass/fail gates (background check, training) stay a muted grey.
type ChipState = 'done' | 'progress' | 'pending';

// Incomplete milestones whose dot goes amber (reads as "in progress") rather than
// the grey dot of a not-yet-started gate.
const PROGRESS_KEYS = ['onboarding', 'shifts', 'i9'];
// Of the progress milestones, these carry the amber on the DOT ONLY — their
// border + text stay neutral, so just the circle is colored. (Onboarding is the
// one progress chip that goes fully amber.)
const DOT_ONLY_PROGRESS_KEYS = ['shifts', 'i9'];
const chipState = (key: string, complete: boolean): ChipState =>
  complete ? 'done' : PROGRESS_KEYS.includes(key) ? 'progress' : 'pending';

const COLORS: Record<
  ChipState,
  { border: string; text: string; icon: string }
> = {
  done: { border: '#44A735', text: '#44A735', icon: '#44A735' },
  progress: { border: '#ECA300', text: '#ECA300', icon: '#ECA300' },
  // grey chip: light border + dark label + a visible (not white) grey icon.
  pending: { border: '#DADADA', text: '#1C1C1C', icon: '#979797' },
};

const Row = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
});
const Chip = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  border: '1px solid transparent',
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 700,
  fontSize: '12px',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
});

// Done shows the heroicons-mini check-circle asset (a green disc with a white
// check). The inline SVG version rendered as a plain green disc: the heroicons
// path needs fill-rule="evenodd" to cut the check out, and without it the check
// disappeared — so "done" uses the PNG (a solid green check-circle). Incomplete
// milestones show a dot in the state color: an amber in-progress dot is a SOLID
// filled disc, while a grey pending-gate dot is an OUTLINE ring (border only, no
// fill) so a not-yet-started gate reads as an empty circle.
const StateIcon = ({
  done,
  color,
  outline,
}: {
  done: boolean;
  color: string;
  outline?: boolean;
}) =>
  done ? (
    <img
      src={checkCircleIcon}
      width={16}
      height={16}
      alt=""
      aria-hidden="true"
      style={{ display: 'block', flex: 'none' }}
    />
  ) : (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      style={{ color, flex: 'none' }}
    >
      {outline ? (
        // border-only ring: r + strokeWidth/2 matches the filled disc's r=7 edge
        <circle
          cx="10"
          cy="10"
          r="6.25"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      ) : (
        <circle cx="10" cy="10" r="7" fill="currentColor" />
      )}
    </svg>
  );

interface Props {
  milestones: ActivationBreakdown['milestones'];
}

const OnboardingChips = ({ milestones }: Props) => (
  <Row data-testid="onboarding-chips">
    {(milestones ?? []).map((m) => {
      const state = chipState(m.key, m.complete);
      const c = COLORS[state];
      // Dot-only progress chips (shifts, I-9) keep neutral chrome — border + text
      // drop to the pending style (grey border, dark label) while the dot stays
      // its amber progress color, so only the circle reads as "in progress".
      // Every other chip keeps border/text in its own state color.
      const chrome =
        state === 'progress' && DOT_ONLY_PROGRESS_KEYS.includes(m.key)
          ? COLORS.pending
          : c;
      return (
        <Chip
          key={m.key}
          style={{ borderColor: chrome.border, color: chrome.text }}
          data-testid={`onboarding-chip-${m.key}`}
          data-state={state}
          data-complete={m.complete || undefined}
        >
          <StateIcon
            done={state === 'done'}
            color={c.icon}
            outline={state === 'pending'}
          />
          {m.label}
        </Chip>
      );
    })}
  </Row>
);

export default OnboardingChips;
